import React from 'react'
import { Skeleton, Empty, message } from 'antd'
import { EditOutlined } from '@ant-design/icons'

import { Container } from "../../Styles"
import { SectionHeader } from '../SectionHeader'
import { Item } from "../Item"
import { StyledButton } from '../Button'
import { ChangePharmacyModal } from './Pharmacy/ChangePharmacyModal'

import { useCaseViewContext } from '../../context'
import { usePharmacyData } from '../../hooks/usePharmacyData'
import { usePharmacyManagement } from '../../hooks/usePharmacyManagement'

export const Pharmacy = () => {
    const { caseDetails, currentPharmacy, currentPharmacyId, updateCurrentPharmacy, apiClasses, apiType, readOnlyMode } = useCaseViewContext()
    const pharmacyId = currentPharmacyId || caseDetails?.pharmacyId
    
    const contextPharmacy = caseDetails?.pharmacy
    const { pharmacy: apiPharmacy, loading, error } = usePharmacyData(pharmacyId, apiClasses)
    
    const displayPharmacy = currentPharmacy || contextPharmacy || apiPharmacy
    
    
    const handlePharmacyChange = async (newPharmacy) => {
        if (apiType === 'admin') {
            message.warning('Pharmacy changes are read-only for admin users')
            return
        }
        
        updateCurrentPharmacy(newPharmacy)
    }
    
    const {
        showChangePharmacyModal,
        pharmacySearchResults,
        pharmacySpecialtyTypes,
        pharmacySearchLoading,
        searchPharmacies,
        selectPharmacy,
        handleCancelSearch,
        showChangePharmacyModalHandler
    } = usePharmacyManagement({
        apiClasses,
        caseDetails,
        onPharmacyChange: handlePharmacyChange
    })

    const formatAddress = (pharmacy) => {
        if (!pharmacy) return 'N/A';
        
        const addressParts = [];
        if (pharmacy.address1) addressParts.push(pharmacy.address1);
        if (pharmacy.address2) addressParts.push(pharmacy.address2);
        if (pharmacy.city) addressParts.push(pharmacy.city);
        if (pharmacy.state) addressParts.push(pharmacy.state);
        if (pharmacy.zipCode) addressParts.push(pharmacy.zipCode);
        
        return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
    };

    const formatPhone = (phone) => {
        if (!phone) return 'N/A';
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return phone;
    };

    return (
        <Container>
            <Skeleton loading={!displayPharmacy} active paragraph={{ rows: 1 }}>
                <SectionHeader title="Pharmacy">
                    {apiType !== 'admin' && !readOnlyMode && (
                        <StyledButton
                            tooltip='Edit Pharmacy Information'
                            onClick={showChangePharmacyModalHandler} 
                            Icon={EditOutlined}
                        />
                    )}
                </SectionHeader>
            </Skeleton>
            
            <Skeleton loading={(!displayPharmacy && loading) || !caseDetails} active paragraph={{ rows: 4 }}>
                {error && !contextPharmacy && (
                    <div style={{ color: '#ff4d4f', marginBottom: '10px' }}>
                        Error loading pharmacy information
                    </div>
                )}
                {!pharmacyId && <Empty description="No pharmacy ID available" />}
                {pharmacyId && !displayPharmacy && !loading && !contextPharmacy && <Empty description="No pharmacy information available" />}
                {displayPharmacy && (
                    <div>
                        <Item label="Name" value={displayPharmacy.name || 'N/A'} />
                        <Item label="Address" value={formatAddress(displayPharmacy)} />
                        <Item label="Phone" value={formatPhone(displayPharmacy.phone || displayPharmacy.phoneOrFax)} />
                        {displayPharmacy.email && <Item label="Email" value={displayPharmacy.email} />}
                        {displayPharmacy.npi && <Item label="NPI" value={displayPharmacy.npi} />}
                    </div>
                )}
            </Skeleton>
            
            <ChangePharmacyModal
                visible={showChangePharmacyModal}
                onCancel={handleCancelSearch}
                pharmacySearchResults={pharmacySearchResults}
                pharmacySpecialtyTypes={pharmacySpecialtyTypes}
                pharmacySearchLoading={pharmacySearchLoading}
                searchPharmacies={searchPharmacies}
                selectPharmacy={selectPharmacy}
                handleCancelSearch={handleCancelSearch}
            />
        </Container>
    )
}
